"use client";

/**
 * Cookie consent (RODO) - blokujący modal zgód + pływający przycisk ustawień.
 *
 * Renderowany przez `RodoConsent` (server) tylko gdy moduł m_rodo aktywny.
 * Stan zgody przekazywany z SSR (`initialState`, cookie czytany server-side),
 * więc brak flash'a modala dla powracających.
 *
 * Dwa tryby:
 *   1. WYMUSZONY (pierwsza wizyta, brak zgody, `show_banner === "1"`): overlay
 *      przykrywa viewport i blokuje scroll/interakcję (`aria-modal`,
 *      `body.overflow=hidden`). Brak przycisku zamknięcia - odwiedzający MUSI
 *      dokonać wyboru (Akceptuję / Odrzuć / Dostosuj) zanim skorzysta z serwisu.
 *   2. PONOWNY (po udzieleniu zgody): pływający przycisk ciasteczka w rogu
 *      strony otwiera ten sam modal do zmiany wyboru. W tym trybie modal jest
 *      zamykalny (przycisk X + klik w overlay) - zgoda już istnieje.
 *
 * Po zatwierdzeniu: zapis cookie + Consent Mode 'update' + injekcja head/body
 * zaakceptowanych kategorii ({@link applyConsent}).
 *
 * @see ../../lib/cookies/consentClient.ts
 * @see ./RodoConsent.tsx
 */

import { useEffect, useState } from "react";
import { useT } from "@/lib/i18n/context";
import { Checkbox } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import type { RodoConsentConfig, RodoCategory } from "@/lib/api/cms-config";
import { type ConsentState } from "@/lib/cookies/consent";
import { applyConsent, writeConsentCookie } from "@/lib/cookies/consentClient";
import { cookieConsentTranslations } from "./CookieConsent.i18n";
import styles from "./CookieConsent.module.css";

type CookieConsentProps = {
  config: RodoConsentConfig;
  /** Zapisany stan zgody z SSR (null = brak zgody -> wymuszony pierwszy wybór). */
  initialState: ConsentState | null;
};

/** Odtwarza zaznaczenia checkboxów z zapisanego stanu zgody (always_active = on). */
function deriveSelected(
  categories: RodoCategory[],
  state: ConsentState | null,
): Record<number, boolean> {
  const init: Record<number, boolean> = {};
  for (const c of categories) {
    init[c.id] =
      c.always_active || (state ? state.all || state.ids.includes(c.id) : false);
  }
  return init;
}

export function CookieConsent({ config, initialState }: CookieConsentProps) {
  const t = useT(cookieConsentTranslations);
  const categories = config.categories ?? [];
  const showButton = config.show_banner === "1";

  // Aktualnie zapisana zgoda (null = brak). Po commit ustawiana na nowy stan.
  const [savedState, setSavedState] = useState<ConsentState | null>(initialState);
  const consented = savedState !== null;

  // Modal otwarty: na starcie tylko gdy wymuszony (brak zgody + banner włączony).
  const [open, setOpen] = useState(showButton && !consented);
  const [customizing, setCustomizing] = useState(false);
  const [selected, setSelected] = useState<Record<number, boolean>>(() =>
    deriveSelected(categories, initialState),
  );
  // Rozwinięty podgląd listy plików cookie per kategoria.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Tryb wymuszony - nie wolno zamknąć modala bez wyboru (pierwsza wizyta).
  const forced = open && !consented;

  // Blokada scrolla strony gdy modal otwarty.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const commit = (state: ConsentState) => {
    writeConsentCookie(state);
    applyConsent(categories, state);
    setSavedState(state);
    setOpen(false);
    setCustomizing(false);
  };

  const acceptAll = () =>
    commit({ v: 1, all: true, ids: categories.map((c) => c.id) });

  const rejectAll = () =>
    commit({
      v: 1,
      all: false,
      ids: categories.filter((c) => c.always_active).map((c) => c.id),
    });

  const savePartial = () => {
    const ids = categories
      .filter((c) => c.always_active || selected[c.id])
      .map((c) => c.id);
    commit({ v: 1, all: ids.length === categories.length, ids });
  };

  // Otwarcie ponowne (z pływającego przycisku) - odtwórz aktualne zaznaczenia.
  const openManage = () => {
    setSelected(deriveSelected(categories, savedState));
    setCustomizing(false);
    setOpen(true);
  };

  const closeManage = () => {
    if (forced) return; // wymuszony wybór - brak zamykania
    setOpen(false);
    setCustomizing(false);
  };

  // Pływający przycisk - gdy zgoda udzielona i modal zamknięty. Niezależny od
  // show_banner (auto-popup) - po wyborze user zawsze może wrócić do ustawień.
  if (!open) {
    if (!consented) return null;
    return (
      <button
        type="button"
        className={styles.floating}
        onClick={openManage}
        aria-label={t.manageButton}
        title={t.manageButton}
      >
        <Icon name="cookie" size={24} />
      </button>
    );
  }

  return (
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-label={t.ariaLabel}
      onClick={forced ? undefined : closeManage}
    >
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        {!forced && (
          <button
            type="button"
            className={styles.close}
            onClick={closeManage}
            aria-label={t.close}
          >
            <Icon name="x" size={20} />
          </button>
        )}

        {!customizing ? (
          <>
            <div className={styles.body}>
              <h2 className={styles.title}>{t.title}</h2>
              {config.consent_text ? (
                <div
                  className={styles.text}
                  dangerouslySetInnerHTML={{ __html: config.consent_text }}
                />
              ) : (
                <p className={styles.text}>{t.defaultText}</p>
              )}
            </div>
            <div className={styles.actions}>
              {categories.length > 0 && (
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => setCustomizing(true)}
                >
                  {t.customize}
                </button>
              )}
              <button type="button" className={styles.btnGhost} onClick={rejectAll}>
                {t.rejectAll}
              </button>
              <button type="button" className={styles.btnPrimary} onClick={acceptAll}>
                {t.acceptAll}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.body}>
              <h2 className={styles.title}>{t.settingsTitle}</h2>
              <ul className={styles.catList}>
                {categories.map((c) => {
                  const cookies = (c.groups ?? []).flatMap((g) => g.cookies ?? []);
                  const isOpen = !!expanded[c.id];
                  return (
                    <li key={c.id} className={styles.catItem}>
                      <Checkbox
                        checked={c.always_active ? true : !!selected[c.id]}
                        disabled={c.always_active}
                        onChange={(e) =>
                          setSelected((s) => ({ ...s, [c.id]: e.target.checked }))
                        }
                      >
                        <span className={styles.catName}>
                          {c.name}
                          {c.always_active ? ` (${t.alwaysActive})` : ""}
                        </span>
                      </Checkbox>
                      {c.description ? (
                        <div
                          className={styles.catDesc}
                          dangerouslySetInnerHTML={{ __html: c.description }}
                        />
                      ) : null}
                      {cookies.length > 0 ? (
                        <div className={styles.cookieSection}>
                          <button
                            type="button"
                            className={styles.cookieToggle}
                            onClick={() =>
                              setExpanded((s) => ({ ...s, [c.id]: !s[c.id] }))
                            }
                            aria-expanded={isOpen}
                          >
                            <Icon
                              name={isOpen ? "caret-up" : "caret-down"}
                              size={14}
                            />
                            {isOpen ? t.hideCookies : t.showCookies} ({cookies.length})
                          </button>
                          {isOpen ? (
                            <ul className={styles.cookieList}>
                              {cookies.map((ck, i) => (
                                <li key={i} className={styles.cookieItem}>
                                  <div className={styles.cookieHead}>
                                    <span className={styles.cookieNameVal}>
                                      {ck.name}
                                    </span>
                                    {ck.type ? (
                                      <span className={styles.cookieType}>
                                        {ck.type}
                                      </span>
                                    ) : null}
                                  </div>
                                  <dl className={styles.cookieFields}>
                                    {ck.host ? (
                                      <div className={styles.cookieField}>
                                        <dt>{t.cookieHost}</dt>
                                        <dd>{ck.host}</dd>
                                      </div>
                                    ) : null}
                                    {ck.time ? (
                                      <div className={styles.cookieField}>
                                        <dt>{t.cookieDuration}</dt>
                                        <dd>{ck.time}</dd>
                                      </div>
                                    ) : null}
                                    {ck.description ? (
                                      <div className={styles.cookieField}>
                                        <dt>{t.cookieDescription}</dt>
                                        <dd>{ck.description}</dd>
                                      </div>
                                    ) : null}
                                  </dl>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => setCustomizing(false)}
              >
                {t.back}
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={savePartial}
              >
                {t.savePreferences}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
